'use client'

import React, { createContext, useContext, useMemo, ReactNode } from 'react'
import { TeamMember } from '@/types'

interface DisplayNamesContextType {
  get: (userId: number | null | undefined, fallback?: string) => string
}

const DisplayNamesContext = createContext<DisplayNamesContextType>({
  get: (_, fallback) => fallback ?? '',
})

interface DisplayNamesProviderProps {
  children: ReactNode
  /** Pre-built userId → displayName map from /agency-memberships/display-names */
  displayNameMap?: Record<string, string>
  /** Full team members (fallback if displayNameMap not provided) */
  initialTeamMembers?: TeamMember[]
}

export function DisplayNamesProvider({ children, displayNameMap, initialTeamMembers }: DisplayNamesProviderProps) {
  const map = useMemo(() => {
    const m = new Map<number, string>()

    // Primary source: lightweight display-names endpoint
    if (displayNameMap) {
      for (const [userId, name] of Object.entries(displayNameMap)) {
        if (name) {
          m.set(Number(userId), name)
        }
      }
    }

    // Fallback: full team members (fills in any users not already in the map)
    if (initialTeamMembers) {
      for (const member of initialTeamMembers) {
        if (member.user?.id && !m.has(member.user.id)) {
          m.set(member.user.id, member.display_name || member.user.username || '')
        }
      }
    }

    return m
  }, [displayNameMap, initialTeamMembers])

  const get = useMemo(
    () =>
      (userId: number | null | undefined, fallback?: string): string => {
        if (userId != null) {
          const name = map.get(userId)
          if (name) return name
        }
        return fallback ?? ''
      },
    [map]
  )

  return (
    <DisplayNamesContext.Provider value={{ get }}>
      {children}
    </DisplayNamesContext.Provider>
  )
}

export function useDisplayNames() {
  return useContext(DisplayNamesContext)
}
